import { Avatar } from 'common/src/components/avatar/avatar';
import { Box } from 'common/src/designSystem/components/box';
import { Flex } from 'common/src/designSystem/components/flex';
import { I } from 'common/src/designSystem/components/i';
import { Skeleton } from 'common/src/designSystem/components/skeleton';
import { EventOmnibarQuery, SegmentId } from 'common/src/generated/types';
import { isEmptyArray, isNonEmptyArray } from 'common/src/util/array';
import { preventDefault } from 'common/src/util/links';
import { AccreditationsPaths } from 'common/src/util/paths/accreditationsPaths';
import { CampaignsPaths } from 'common/src/util/paths/campaignsPaths';
import { DelegationsPaths } from 'common/src/util/paths/delegationsPaths';
import { FormsPaths } from 'common/src/util/paths/formsPaths';
import { HeaventPaths } from 'common/src/util/paths/heaventPaths';
import { PositionsPaths } from 'common/src/util/paths/positionsPaths';
import { UsersPaths } from 'common/src/util/paths/usersPaths';
import { WorkflowsPaths } from 'common/src/util/paths/workflowsPaths';
import { isNonEmptyString, normalize } from 'common/src/util/string';
import * as React from 'react';
import { createPortal } from 'react-dom';
import { TextInput } from '../../designSystem/components/textInput';
import {
    executeEventOmnibarQuery,
    executeOrganizationOmnibarQuery
} from '../../generated/graphqlHooks';
import { useBodyClick } from '../../hooks/useBodyClick';
import { useEsc } from '../../hooks/useEsc';
import { useHeavent } from '../../hooks/useHeavent';
import { useShortcut } from '../../hooks/useShortcut';
import { useStateDebounce } from '../../hooks/useStateDebounce';
import { getToken } from '../../util/aws/cognito';
import {
    getOmnibarEventActions,
    getOmnibarOrganizationActions,
    OmnibarAction
} from './omnibarActions';
import { OmnibarRow } from './omnibarRow';
import { OmnibarSection } from './omnibarSection';

const H_OPEN_OMNIBAR = 'h-open-omnibar';

export function openOmnibar(element: { dispatchEvent(event: Event): boolean }) {
    element.dispatchEvent(new CustomEvent(H_OPEN_OMNIBAR, { bubbles: true }));
}

type OmnibarMember =
    EventOmnibarQuery['event']['volunteersRegistrations']['nodes'][number]['userInfo'];
type OmnibarEvent = EventOmnibarQuery['organization']['events']['nodes'][number];
type OmnibarCustomField = EventOmnibarQuery['organization']['customFields']['nodes'][number];
type OmnibarPosition = EventOmnibarQuery['event']['positions']['nodes'][number];
type OmnibarForm = EventOmnibarQuery['event']['forms']['nodes'][number];
type OmnibarAccreditation = EventOmnibarQuery['event']['accreditations']['nodes'][number];
type OmnibarDelegation = EventOmnibarQuery['event']['delegations']['nodes'][number];
type OmnibarCampaign = EventOmnibarQuery['event']['campaigns']['nodes'][number];
type OmnibarWorkflow = EventOmnibarQuery['event']['workflows']['nodes'][number];

export const Omnibar = () => {
    const {
        translate,
        params: { organizationId, eventId }
    } = useHeavent();
    const [isOpen, setIsOpen] = React.useState(false);
    const [isLoading, setIsLoading] = React.useState(false);
    const [search, searchDebounced, setSearch] = useStateDebounce('');
    const [actions, setActions] = React.useState<OmnibarAction[]>(
        eventId
            ? getOmnibarEventActions(translate, organizationId, eventId)
            : getOmnibarOrganizationActions(translate, organizationId)
    );
    const [members, setMembers] = React.useState<OmnibarMember[]>([]);
    const [events, setEvents] = React.useState<OmnibarEvent[]>([]);
    const [customFields, setCustomFields] = React.useState<OmnibarCustomField[]>([]);
    const [positions, setPositions] = React.useState<OmnibarPosition[]>([]);
    const [forms, setForms] = React.useState<OmnibarForm[]>([]);
    const [accreditations, setAccreditations] = React.useState<OmnibarAccreditation[]>([]);
    const [delegations, setDelegations] = React.useState<OmnibarDelegation[]>([]);
    const [campaigns, setCampaigns] = React.useState<OmnibarCampaign[]>([]);
    const [workflows, setWorkflow] = React.useState<OmnibarWorkflow[]>([]);
    const allEmpty = React.useMemo(
        () =>
            isEmptyArray(actions) &&
            isEmptyArray(members) &&
            isEmptyArray(events) &&
            isEmptyArray(customFields) &&
            isEmptyArray(positions) &&
            isEmptyArray(forms) &&
            isEmptyArray(accreditations) &&
            isEmptyArray(delegations) &&
            isEmptyArray(campaigns) &&
            isEmptyArray(workflows),
        [
            actions,
            members,
            events,
            customFields,
            positions,
            forms,
            accreditations,
            delegations,
            campaigns,
            workflows
        ]
    );
    const reset = React.useCallback(() => {
        setActions(
            eventId
                ? getOmnibarEventActions(translate, organizationId, eventId)
                : getOmnibarOrganizationActions(translate, organizationId)
        );
        setMembers([]);
        setEvents([]);
        setCustomFields([]);
        setPositions([]);
        setForms([]);
        setAccreditations([]);
        setDelegations([]);
        setCampaigns([]);
        setWorkflow([]);
    }, [
        organizationId,
        eventId,
        setActions,
        setMembers,
        setEvents,
        setCustomFields,
        setPositions,
        setForms,
        setAccreditations,
        setDelegations,
        setCampaigns,
        setWorkflow
    ]);
    const onK = React.useCallback(
        (e: KeyboardEvent) => {
            preventDefault(e);
            setIsLoading(false);
            setSearch('');
            reset();
            setIsOpen((currentIsOpen) => !currentIsOpen);
        },
        [setIsOpen, setIsLoading, setSearch, reset]
    );
    const onClose = React.useCallback(() => {
        setIsOpen(false);
    }, [setIsOpen]);

    useShortcut('k', onK);
    useEsc(onClose);
    useBodyClick(onClose);

    React.useEffect(() => {
        document.addEventListener(H_OPEN_OMNIBAR, onK as any);

        return () => {
            document.removeEventListener(H_OPEN_OMNIBAR, onK as any);
        };
    }, [onK]);

    React.useEffect(() => {
        (async () => {
            if (isNonEmptyString(searchDebounced)) {
                setIsLoading(true);

                if (eventId) {
                    const { organization, event } = await executeEventOmnibarQuery(
                        { organizationId, eventId, name: searchDebounced },
                        await getToken()
                    );

                    setActions(
                        getOmnibarEventActions(translate, organizationId, eventId).filter(
                            (action) =>
                                normalize(action.text.toLowerCase()).includes(
                                    normalize(searchDebounced).toLowerCase()
                                )
                        )
                    );
                    setMembers(event.volunteersRegistrations.nodes.map((vr) => vr.userInfo));
                    setEvents(organization.events.nodes);
                    setCustomFields(organization.customFields.nodes);
                    setPositions(event.positions.nodes);
                    setForms(event.forms.nodes);
                    setAccreditations(event.accreditations.nodes);
                    setDelegations(event.delegations.nodes);
                    setCampaigns(event.campaigns.nodes);
                    setWorkflow(event.workflows.nodes);
                } else {
                    const { organization } = await executeOrganizationOmnibarQuery(
                        { organizationId, name: searchDebounced },
                        await getToken()
                    );

                    setActions(
                        getOmnibarOrganizationActions(translate, organizationId).filter((action) =>
                            normalize(action.text.toLowerCase()).includes(
                                normalize(searchDebounced).toLowerCase()
                            )
                        )
                    );
                    setMembers(organization.usersInfos.nodes);
                    setEvents(organization.events.nodes);
                    setCustomFields(organization.customFields.nodes);
                    setPositions(organization.positions.nodes);
                    setForms(organization.forms.nodes);
                    setAccreditations(organization.accreditations.nodes);
                    setDelegations(organization.delegations.nodes);
                    setCampaigns(organization.campaigns.nodes);
                    setWorkflow(organization.workflows.nodes);
                }

                setIsLoading(false);
            } else {
                reset();
            }
        })();
    }, [
        organizationId,
        eventId,
        searchDebounced,
        setIsLoading,
        setActions,
        setMembers,
        setEvents,
        setCustomFields,
        setPositions,
        setForms,
        setAccreditations,
        setDelegations,
        setCampaigns,
        setWorkflow,
        reset
    ]);

    if (isOpen) {
        return createPortal(
            <Flex
                align="center"
                css={{
                    background: 'rgba(52, 64, 84, 0.7)',
                    height: '100%',
                    left: 0,
                    position: 'absolute',
                    top: 0,
                    width: '100%',
                    zIndex: '300'
                }}
                justify="center"
            >
                <Flex
                    css={{
                        background: 'white',
                        borderRadius: '$3',
                        overflow: 'hidden'
                    }}
                    direction="column"
                    width={640}
                    onClick={(e) => preventDefault(e)}
                >
                    <Box
                        css={{
                            '& .h-styled-input-container': {
                                height: '48px'
                            }
                        }}
                        height={48}
                    >
                        <TextInput
                            autoFocus={true}
                            css={{
                                borderBottom: '1px solid $gray200',
                                '& .h-styled-input-container': {
                                    border: 'none'
                                }
                            }}
                            height="fluid"
                            icon="magnifying-glass"
                            placeholder={translate('rechercher_un_m_45772')}
                            value={search}
                            onChange={setSearch}
                        />
                    </Box>

                    <Flex
                        css={{
                            overflowY: 'auto',
                            py: '$4'
                        }}
                        direction="column"
                        height={350}
                    >
                        {isLoading ? (
                            <Flex direction="column" gap="2">
                                <Skeleton borderRadius="$1" height={36} />
                                <Skeleton borderRadius="$1" height={36} />
                                <Skeleton borderRadius="$1" height={36} />
                            </Flex>
                        ) : allEmpty ? (
                            <Flex
                                align="center"
                                direction="column"
                                gap="4"
                                height={1}
                                justify="center"
                            >
                                <Flex
                                    align="center"
                                    css={{
                                        bd: true,
                                        fontSize: '$textMd'
                                    }}
                                    height={48}
                                    justify="center"
                                    width={48}
                                >
                                    <I icon="magnifying-glass" />
                                </Flex>

                                <Box font="gray900 textSm semiBold">
                                    {translate('aucun_r_sultat_73664', searchDebounced)}
                                </Box>
                            </Flex>
                        ) : (
                            <Flex direction="column" gap="6">
                                {isNonEmptyArray(actions) && (
                                    <OmnibarSection title={translate('actions_rapides_42479')}>
                                        {actions.map((action, index) => (
                                            <OmnibarRow
                                                key={index}
                                                icon={action.icon}
                                                link={action.link}
                                                onClose={onClose}
                                            >
                                                {action.text}
                                            </OmnibarRow>
                                        ))}
                                    </OmnibarSection>
                                )}

                                {isNonEmptyArray(members) && (
                                    <OmnibarSection title={translate('membres_11310')}>
                                        {members.map((member) => (
                                            <OmnibarRow
                                                key={member.id}
                                                avatar={
                                                    <Avatar
                                                        email={member.email}
                                                        image={member.picture?.url}
                                                        name={member.name}
                                                        size={24}
                                                    />
                                                }
                                                link={UsersPaths.USER_INFORMATIONS({
                                                    organizationId,
                                                    eventId,
                                                    userInfoId: member.id
                                                })}
                                                onClose={onClose}
                                            >
                                                {member.name}
                                            </OmnibarRow>
                                        ))}
                                    </OmnibarSection>
                                )}

                                {isNonEmptyArray(events) && (
                                    <OmnibarSection title={translate('Event')}>
                                        {events.map((event) => (
                                            <OmnibarRow
                                                key={event.id}
                                                icon="cake-candles"
                                                link={HeaventPaths.EDIT_EVENT(
                                                    organizationId,
                                                    event.id
                                                )}
                                                onClose={onClose}
                                            >
                                                {event.name}
                                            </OmnibarRow>
                                        ))}
                                    </OmnibarSection>
                                )}

                                {isNonEmptyArray(customFields) && (
                                    <OmnibarSection title={translate('champs_de_formu_64126')}>
                                        {customFields.map((customField) => (
                                            <OmnibarRow
                                                key={customField.id}
                                                icon="text"
                                                link={HeaventPaths.EDIT_CUSTOM_FIELD(
                                                    organizationId,
                                                    -1 as SegmentId,
                                                    customField.id
                                                )}
                                                onClose={onClose}
                                            >
                                                {customField.name}
                                            </OmnibarRow>
                                        ))}
                                    </OmnibarSection>
                                )}

                                {isNonEmptyArray(positions) && (
                                    <OmnibarSection title={translate('missions_63972')}>
                                        {positions.map((position) => (
                                            <OmnibarRow
                                                key={position.id}
                                                icon="sitemap"
                                                link={PositionsPaths.POSITION({
                                                    organizationId,
                                                    eventId,
                                                    positionId: position.id
                                                })}
                                                onClose={onClose}
                                            >
                                                {position.name}
                                            </OmnibarRow>
                                        ))}
                                    </OmnibarSection>
                                )}

                                {isNonEmptyArray(forms) && (
                                    <OmnibarSection title={translate('Form_plural')}>
                                        {forms.map((form) => (
                                            <OmnibarRow
                                                key={form.id}
                                                icon="file-lines"
                                                link={FormsPaths.FORM({
                                                    organizationId,
                                                    eventId,
                                                    formId: form.id
                                                })}
                                                onClose={onClose}
                                            >
                                                {form.name}
                                            </OmnibarRow>
                                        ))}
                                    </OmnibarSection>
                                )}

                                {isNonEmptyArray(accreditations) && (
                                    <OmnibarSection title={translate('accr_ditations_39450')}>
                                        {accreditations.map((accreditation) => (
                                            <OmnibarRow
                                                key={accreditation.id}
                                                icon="badge-check"
                                                link={AccreditationsPaths.ACCREDITATION({
                                                    organizationId,
                                                    eventId,
                                                    accreditationId: accreditation.id
                                                })}
                                                onClose={onClose}
                                            >
                                                {accreditation.name}
                                            </OmnibarRow>
                                        ))}
                                    </OmnibarSection>
                                )}

                                {isNonEmptyArray(delegations) && (
                                    <OmnibarSection title={translate('d_l_gations_78318')}>
                                        {delegations.map((delegation) => (
                                            <OmnibarRow
                                                key={delegation.id}
                                                icon="users"
                                                link={DelegationsPaths.DELEGATION({
                                                    organizationId,
                                                    eventId,
                                                    delegationId: delegation.id
                                                })}
                                                onClose={onClose}
                                            >
                                                {delegation.name}
                                            </OmnibarRow>
                                        ))}
                                    </OmnibarSection>
                                )}

                                {isNonEmptyArray(campaigns) && (
                                    <OmnibarSection title={translate('Campaigns')}>
                                        {campaigns.map((campaign) => (
                                            <OmnibarRow
                                                key={campaign.id}
                                                icon="paper-plane"
                                                link={CampaignsPaths.CAMPAIGN_DASHBOARD({
                                                    organizationId,
                                                    eventId,
                                                    campaignId: campaign.id
                                                })}
                                                onClose={onClose}
                                            >
                                                {campaign.name}
                                            </OmnibarRow>
                                        ))}
                                    </OmnibarSection>
                                )}

                                {isNonEmptyArray(workflows) && (
                                    <OmnibarSection title={translate('automatisations_60045')}>
                                        {workflows.map((workflow) => (
                                            <OmnibarRow
                                                key={workflow.id}
                                                icon="hand-sparkles"
                                                link={WorkflowsPaths.UPDATE({
                                                    organizationId,
                                                    eventId,
                                                    workflowId: workflow.id
                                                })}
                                                onClose={onClose}
                                            >
                                                {workflow.name}
                                            </OmnibarRow>
                                        ))}
                                    </OmnibarSection>
                                )}
                            </Flex>
                        )}
                    </Flex>
                </Flex>
            </Flex>,
            document.body
        );
    } else {
        return null;
    }
};
